import { useEffect, useRef, useState } from 'react';
import { useRouter } from 'next/router';
import { useTranslation } from 'next-i18next';
import classNames from 'classnames';
import Image from 'next/image';
import { Locale, LocaleFlagsMapping } from '../../../core/constants';
import { useUpdateAttributes } from '../../../core/account/AuthServiceHooks';
import cookie from '../../../core/helpers/cookie';
import SkipTranslationWrapper from '../SkipTranslationWrapper';

const LanguageDropdown = () => {
  const { t } = useTranslation(['common']);
  const ref = useRef<HTMLDivElement>(null);
  const [updateAttributes] = useUpdateAttributes();
  const router = useRouter();

  const [showDropdown, setShowDropdown] = useState<boolean>(false);

  const events = {
    onClickDropdown: () => {
      setShowDropdown(!showDropdown);
    },
    onCloseDropdown: (event: MouseEvent) => {
      if (ref.current && !ref.current.contains(event.target as Node)) {
        setShowDropdown(false);
      }
    },
    onClickLanguage: async (locale: string) => {
      const { pathname, query } = router as { pathname: string; query: Record<string, string> };
      try {
        const attributes = [
          {
            Name: 'locale' as const,
            Value: locale,
          },
        ];
        await updateAttributes(attributes);
      } catch (error) {
        /* eslint-disable no-console */
        console.log('%cupdate attribute error:', 'color:yellow', error);
      }
      cookie.locale.set(locale as string);
      const params = new URLSearchParams(query as Record<string, string>);

      // Remove source and locale parameters as it was added by Google Hotel Ads to force redirect to certain locale.
      params.delete('source');
      params.delete('locale');

      router.replace({ pathname, query: params.toString() }, undefined, { locale });
      setShowDropdown(false);
    },
  };

  useEffect(() => {
    document.addEventListener('mousedown', events.onCloseDropdown);
    return () => {
      document.removeEventListener('mousedown', events.onCloseDropdown);
    };
  });

  const currentLocaleTip = () => {
    return (
      <>
        <Image
          src={LocaleFlagsMapping[router.locale as string] as string}
          alt={LocaleFlagsMapping[router.locale as string] as string}
          width={32}
          height={32}
          className="cursor-pointer"
        />
        <p className="text-base font-bold">{t(`enum.Locale.${router.locale}`)}</p>
      </>
    );
  };

  return (
    <div className="relative flex cursor-pointer select-none" ref={ref}>
      <div
        className="flex items-center"
        onClick={events.onClickDropdown}
        onKeyDown={events.onClickDropdown}
        role="button"
        aria-label="Language Dropdown"
        tabIndex={0}
      >
        <SkipTranslationWrapper className="flex h-10 items-center gap-2 rounded-full bg-white p-1 pr-4 hover:bg-gray-50">
          {currentLocaleTip()}
        </SkipTranslationWrapper>
      </div>
      {showDropdown && (
        <div className="absolute right-0 top-14 z-50 flex min-w-[202px] flex-col rounded-lg bg-white py-2 shadow-lg">
          {Object.values(Locale).map((value) => (
            <SkipTranslationWrapper
              key={value}
              className={classNames(
                'flex items-center p-2 px-4 text-small hover:underline w-full gap-2',
                {
                  'bg-red-50 font-bold': value === router.locale,
                }
              )}
              onClick={() => events.onClickLanguage(value)}
              onKeyDown={() => events.onClickLanguage(value)}
              role="button"
              tabIndex={0}
            >
              <Image
                src={LocaleFlagsMapping[value] as string}
                alt={LocaleFlagsMapping[value] as string}
                width={24}
                height={24}
                className="cursor-pointer"
              />
              {t(`enum.Locale.${value}`)}
            </SkipTranslationWrapper>
          ))}
        </div>
      )}
    </div>
  );
};

export default LanguageDropdown;
